'use client';
import React, { useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  getTrainingVideos,
  formatCurrency,
} from '@/lib/supabaseService';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { Trophy, TrendingUp, ShoppingBag, Medal, BadgeCheck } from 'lucide-react';

interface LeaderEntry {
  rank: number;
  id: string;
  fullName: string;
  totalOrders: number;
  dropserviceEarnings: number;
  referralCount: number;
  isCurrentUser: boolean;
  trainingCompleted: boolean;
}

type SortKey = 'orders' | 'revenue';

// Module-level client — not recreated on re-renders
const supabase = createClient();

export default function LeaderboardClient() {
  const router = useRouter();
  const { user: authUser, loading: authLoading } = useAuth();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [entries, setEntries] = useState<LeaderEntry[]>([]);
  const [sortBy, setSortBy] = useState<SortKey>('orders');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!authUser) { router.replace('/sign-up-login-screen'); return; }

    const load = async () => {
      try {
        // Fetch all non-admin, non-suspended users directly via supabase client
        // The new RLS policy "authenticated_read_all_profiles" allows this
        const [profilesResult, videos] = await Promise.all([
          supabase
            .from('user_profiles')
            .select('id, full_name, total_orders, dropservice_earnings, affiliate_earnings, referred_by, referral_code, is_admin, suspended, payment_status')
            .eq('is_admin', false)
            .eq('suspended', false),
          getTrainingVideos(),
        ]);
        const allProfiles = profilesResult.data;

        if (!allProfiles) {
          setLoading(false);
          return;
        }

        // Fetch training progress for all users
        const { data: allProgress } = await supabase
          .from('training_progress')
          .select('user_id, video_id, completed');

        const totalVideos = videos.length;

        // Build a map of userId → completedCount
        const progressMap: Record<string, number> = {};
        (allProgress || []).forEach((p: any) => {
          if (p.completed) {
            progressMap[p.user_id] = (progressMap[p.user_id] || 0) + 1;
          }
        });

        // Only show active (paid) members on leaderboard
        const activeMembers = allProfiles.filter((u) => u.payment_status === 'paid');

        const withReferrals = activeMembers.map((u) => {
          const completedCount = progressMap[u.id] || 0;
          const referralCount = allProfiles.filter((x) => x.referred_by === u.referral_code).length;
          return {
            id: u.id,
            fullName: u.full_name,
            totalOrders: u.total_orders || 0,
            dropserviceEarnings: Number(u.dropservice_earnings) || 0,
            referralCount,
            isCurrentUser: u.id === authUser.id,
            trainingCompleted: totalVideos > 0 && completedCount >= totalVideos,
          };
        });

        setCurrentUserId(authUser.id);
        setEntries(withReferrals);
      } catch (err) {
        console.error('[Leaderboard] load error:', err);
      }
      setLoading(false);
    };

    load();
  }, [authUser, authLoading, router]);

  const sorted = [...entries].sort((a, b) =>
    sortBy === 'orders'
      ? b.totalOrders - a.totalOrders || b.dropserviceEarnings - a.dropserviceEarnings
      : b.dropserviceEarnings - a.dropserviceEarnings || b.totalOrders - a.totalOrders
  ).map((e, i) => ({ ...e, rank: i + 1 }));

  const currentUserEntry = sorted.find((e) => e.isCurrentUser);

  const rankIcon = (rank: number) => {
    if (rank === 1) return <Trophy size={16} className="text-yellow-500" />;
    if (rank === 2) return <Medal size={16} className="text-slate-400" />;
    if (rank === 3) return <Medal size={16} className="text-amber-600" />;
    return <span className="text-xs font-bold text-muted-foreground w-4 text-center">{rank}</span>;
  };

  const rankBg = (rank: number, isCurrentUser: boolean) => {
    if (isCurrentUser) return 'bg-primary/5 border-primary/20';
    if (rank === 1) return 'bg-yellow-50/50 dark:bg-yellow-900/10 border-yellow-200/50 dark:border-yellow-800/30';
    if (rank === 2) return 'bg-slate-50/50 dark:bg-slate-800/20 border-slate-200/50 dark:border-slate-700/30';
    if (rank === 3) return 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200/50 dark:border-amber-800/30';
    return 'bg-card border-border';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout activeRoute="/leaderboard">
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Leaderboard</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Top members ranked by performance. Keep pushing to climb the ranks!</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Rank by:</span>
          <div className="flex items-center bg-muted rounded-lg p-0.5 gap-0.5">
            {(['orders', 'revenue'] as SortKey[]).map((key) => (
              <button key={key} onClick={() => setSortBy(key)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${sortBy === key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                {key === 'orders' ? <><ShoppingBag size={12} /> Orders</> : <><TrendingUp size={12} /> Revenue</>}
              </button>
            ))}
          </div>
        </div>
        {currentUserEntry && currentUserEntry.rank > 10 && (
          <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><span className="text-xs font-bold text-primary">#{currentUserEntry.rank}</span></div>
              <div><p className="text-sm font-semibold text-foreground">Your Rank</p><p className="text-xs text-muted-foreground">{currentUserEntry.totalOrders} orders · {formatCurrency(currentUserEntry.dropserviceEarnings)}</p></div>
            </div>
            <span className="text-xs text-muted-foreground">{currentUserEntry.rank - 1} member{currentUserEntry.rank - 1 !== 1 ? 's' : ''} ahead</span>
          </div>
        )}
        {sorted.length >= 3 && (
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {[1, 0, 2].map((pos) => {
              const entry = sorted[pos];
              if (!entry) return null;
              const isFirst = pos === 0;
              return (
                <div key={entry.id} className={`bg-card border border-border rounded-xl p-3 sm:p-4 flex flex-col items-center gap-1.5 ${isFirst ? '-mt-2 sm:-mt-3 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800/40' : ''} ${pos === 1 ? 'order-1' : pos === 0 ? 'order-0' : 'order-2'}`}>
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${isFirst ? 'bg-yellow-100 dark:bg-yellow-900/40 w-10 h-10 sm:w-12 sm:h-12' : pos === 1 ? 'bg-slate-100 dark:bg-slate-800' : 'bg-amber-100 dark:bg-amber-900/30'}`}>
                    {isFirst ? <Trophy size={20} className="text-yellow-500" /> : pos === 1 ? <Medal size={18} className="text-slate-400" /> : <Medal size={18} className="text-amber-600" />}
                  </div>
                  <div className="flex items-center gap-1">
                    <p className={`text-xs font-semibold text-foreground text-center truncate leading-tight ${isFirst ? 'font-bold' : ''}`}>{entry.fullName.split(' ')[0]}</p>
                    {entry.trainingCompleted && <BadgeCheck size={13} className="text-blue-500 flex-shrink-0" title="Training Verified" />}
                  </div>
                  <p className="text-[10px] text-muted-foreground">{sortBy === 'orders' ? `${entry.totalOrders} orders` : formatCurrency(entry.dropserviceEarnings)}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isFirst ? 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/40' : pos === 1 ? 'text-slate-500 bg-slate-100 dark:bg-slate-800' : 'text-amber-600 bg-amber-100 dark:bg-amber-900/30'}`}>#{pos + 1}</span>
                </div>
              );
            })}
          </div>
        )}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-4 sm:px-5 py-3.5 border-b border-border"><h2 className="text-sm font-semibold text-foreground">All Rankings</h2></div>
          {sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 gap-3">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center"><Trophy size={22} className="text-muted-foreground" /></div>
              <p className="text-sm font-medium text-foreground">No members yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {sorted.map((entry) => (
                <div key={entry.id} className={`flex items-center gap-3 px-4 sm:px-5 py-3 sm:py-3.5 border-l-2 transition-colors ${rankBg(entry.rank, entry.isCurrentUser)} ${entry.isCurrentUser ? 'border-l-primary' : 'border-l-transparent'}`}>
                  <div className="w-6 flex items-center justify-center flex-shrink-0">{rankIcon(entry.rank)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className={`text-sm text-foreground truncate ${entry.isCurrentUser ? 'font-bold' : 'font-medium'}`}>{entry.fullName}</p>
                      {entry.trainingCompleted && <BadgeCheck size={14} className="text-blue-500 flex-shrink-0" title="Training Verified" />}
                      {entry.isCurrentUser && <span className="text-[10px] font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded-full flex-shrink-0">You</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{entry.totalOrders} orders · {entry.referralCount} referrals</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-foreground">{formatCurrency(entry.dropserviceEarnings)}</p>
                    <p className="text-xs text-muted-foreground">earnings</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
